import React, { useRef } from 'react';
import { MessageCircleHeart, Mic, Minus } from 'lucide-react';
import { useT } from '../../i18n';
import styles from './SearchbarPill.module.css';

export type SearchbarPillLeftAction =
  | {
      kind: 'toggle-templates';
      collapsed: boolean;
      onClick: () => void;
      /** When set, replaces the collapsed-state icon with the AI avatar image. */
      aiAvatarUrl?: string | null;
      /** Configured icon for the collapsed state; takes precedence over the AI avatar. */
      expandIconUrl?: string | null;
      /** Configured icon for the expanded state, replacing the minus glyph. */
      collapseIconUrl?: string | null;
    }
  /** Static AI avatar (no toggle) — keeps the avatar visible mid-conversation. */
  | { kind: 'avatar'; aiAvatarUrl?: string | null }
  | { kind: 'none' };

export interface SearchbarPillProps {
  value: string;
  /** Input placeholder (defaults to the localized "Ask anything…") */
  placeholder?: string;
  disabled?: boolean;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  leftAction: SearchbarPillLeftAction;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

export function SearchbarPill({
  value,
  placeholder,
  disabled = false,
  onInputChange,
  onSubmit,
  leftAction,
  inputRef,
}: SearchbarPillProps) {
  const t = useT();
  const fallbackRef = useRef<HTMLInputElement>(null);
  const ref = inputRef || fallbackRef;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled || value.trim().length === 0) return;
    onSubmit();
  };

  // The collapsed toggle offers "expand" and vice versa, so each state reads its
  // own configured icon. The AI avatar stays in the collapsed chain: agents that
  // set only an avatar keep the pill they already have.
  const toggleIconUrl =
    leftAction.kind === 'toggle-templates'
      ? leftAction.collapsed
        ? leftAction.expandIconUrl || leftAction.aiAvatarUrl
        : leftAction.collapseIconUrl
      : null;

  return (
    <form className={styles.pill} onSubmit={handleSubmit}>
      <div className={styles.inner}>
        {leftAction.kind === 'toggle-templates' && (
          <button
            type="button"
            className={styles.iconButton}
            aria-label={
              leftAction.collapsed ? 'Mostrar sugerencias' : 'Ocultar sugerencias'
            }
            aria-expanded={!leftAction.collapsed}
            onClick={leftAction.onClick}
          >
            {toggleIconUrl ? (
              <img
                src={toggleIconUrl}
                alt=""
                aria-hidden="true"
                className={styles.avatar}
                draggable={false}
              />
            ) : leftAction.collapsed ? (
              <MessageCircleHeart size={17} aria-hidden="true" />
            ) : (
              <Minus size={17} aria-hidden="true" />
            )}
          </button>
        )}

        {leftAction.kind === 'avatar' && leftAction.aiAvatarUrl && (
          <span className={styles.avatarSlot}>
            <img
              src={leftAction.aiAvatarUrl}
              alt=""
              aria-hidden="true"
              className={styles.avatar}
              draggable={false}
            />
          </span>
        )}

        <input
          ref={ref}
          className={styles.input}
          type="text"
          value={value}
          placeholder={placeholder || t('searchbar.placeholder')}
          disabled={disabled}
          onChange={(e) => onInputChange(e.target.value)}
          aria-label={t('searchbar.aiModeSearch')}
        />

        <div className={styles.rightCluster}>
          <button
            type="button"
            className={styles.micButton}
            aria-label={t('searchbar.voiceSearch')}
            tabIndex={-1}
          >
            <Mic size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    </form>
  );
}
